import { useEffect, useState } from "react";
import { Spinner } from "@radix-ui/themes";
import formatPrice from "@/core/helpers/formatPrice";
import { BankCardIcon } from "@/shared/icons";
import Button from "@/shared/ui/button/Button";
import { getFeeData } from "@/domains/bank/helpers";
import toast from "@/domains/toasts/helpers/toast";
import { useBankCharacterQuery } from "@/domains/bank/hooks/useBankCharacterQuery";
import { useBankTransferMutation } from "@/domains/bank/hooks/useBankTransferMutation";
import { ATMInput } from "@/domains/bank/components/BankATM/components/ATMInput";
import { BankBranchCardBalance } from "@/domains/bank/components/BankBranch/components/BankBranchCardBalance";

export function BankBranchTransferFormContainer({
  branchId,
  cardBalance,
  onCancel,
}: {
  branchId: number;
  cardBalance: number;
  onCancel: () => void;
}) {
  const [accountNumber, setAccountNumber] = useState<string>("");
  const [transferAmount, setTransferAmount] = useState<number>(undefined);
  const { mutate, isPending } = useBankTransferMutation();
  const bankCharacterQuery = useBankCharacterQuery({ enabled: true });

  const ownAccountNumber = bankCharacterQuery.data?.accountNumber;
  const transferFeePercent = bankCharacterQuery.data?.tariff?.transferFeePercent || 0;
  const transferLimit = bankCharacterQuery.data?.tariff?.transferLimit || 0;

  const { feeAmount, transferWithFeeAmount, maxAmountBeforeFee } = getFeeData({
    balance: cardBalance,
    feePercent: transferFeePercent,
    transferAmount,
    transferLimit,
    minFeeAmount: 100,
  });

  function handleAmountChange(value: string) {
    const int = Number.parseInt(value, 10);
    setTransferAmount(int);
  }

  function handleAccountNumberChange(value: string) {
    setAccountNumber(value);
  }

  function handleMaxClick() {
    setTransferAmount(Math.min(maxAmountBeforeFee, transferLimit));
  }

  function handleClose() {
    setAccountNumber("");
    setTransferAmount(undefined);
    onCancel();
  }

  function handleSubmit() {
    if (accountNumber === ownAccountNumber) {
      toast.error("Невозможно перевести деньги на свой счет!");
      return;
    }

    if (!transferAmount || transferAmount <= 0 || transferAmount > transferLimit) {
      toast.error("Введите корректную сумму перевода");
      return;
    }

    if (transferWithFeeAmount > cardBalance) {
      toast.error("Недостаточно средств на счете");
      return;
    }

    mutate(
      {
        transferAmount: transferWithFeeAmount,
        accountNumber,
        atmId: null,
        filialId: branchId,
      },
      {
        onSuccess: () => {
          toast.success("Перевод средств выполнен успешно");
          handleClose();
        },
        onError: () => {
          toast.error("Перевод средств не выполнен");
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full">
      <button
        type="button"
        className="relative group rounded-[12px] p-6 bg-dark-800 bg-gradient-to-r from-[#162319] from-0% via-dark-800 via-40%"
      >
        <BankBranchCardBalance hideBalance={false} balance={cardBalance} className="text-left" />
      </button>

      <div className="flex flex-col gap-4">
        <div>
          <div className="text-16 font-normal text-white mb-2">Номер счета:</div>
          <ATMInput
            variant="dark"
            type="text"
            value={accountNumber}
            onChange={(value) => handleAccountNumberChange(value)}
            placeholder="Введите номер"
            icon={<BankCardIcon className="size-6 text-white/30" />}
          />
        </div>

        <div>
          <div className="text-16 font-normal text-white mb-2">Сумма перевода:</div>
          <ATMInput
            variant="dark"
            value={transferAmount?.toString()}
            onChange={(value) => handleAmountChange(value)}
            onMaxClick={() => handleMaxClick()}
            placeholder="Сумма в рублях"
          />
        </div>

        <div className="flex items-center justify-between px-1">
          <div className="text-16 font-normal text-white/70">Комиссия за перевод:</div>
          <div className="text-16 font-medium text-sbank">{feeAmount} ₽</div>
        </div>
      </div>

      <div className="mt-auto">
        <div className="text-right mb-5">
          <div className="text-24 font-bold text-sbank tabular-nums">
            {formatPrice(transferWithFeeAmount)} ₽
          </div>
          <div className="text-14 font-normal text-white/50">Будет списано с учетом комиссии</div>
        </div>

        <footer className="grid grid-cols-2 gap-2 h-[44px]">
          <Button
            variant="solid-gradient"
            data-mz-accent="green"
            disabled={
              isPending || !transferAmount || !accountNumber || transferWithFeeAmount > cardBalance
            }
            onClick={() => handleSubmit()}
          >
            {isPending ? <Spinner className="size-5 animate-spin" /> : "Выполнить"}
          </Button>

          <Button
            variant="solid-outline"
            data-mz-accent="green"
            className="bg-[#21a0381a] text-sbank border-none hover:bg-[#21a0381a]/12"
            onClick={() => handleClose()}
          >
            Отмена
          </Button>
        </footer>
      </div>
    </div>
  );
}
